import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import MarkdownRenderer from './MarkdownRenderer'
import PersonaAvatar from './PersonaAvatar'
import ChatterPlayer from './ChatterPlayer'
import { useAuth } from '../context/AuthContext'
import { useSSEConnected } from '../hooks/useSSE'

interface Message {
  id: string
  sender: string
  content: string
  created_at: string
  is_system?: boolean
}

interface HomeChatTheme {
  accentColor: string
  bgGradient: string
  myBubbleBg: string
  myBubbleBorder: string
  visitorBubbleBg: string
  inputBg: string
  inputFocusGlow: string
}

interface Props {
  personaName: string
  displayName: string
  theme: HomeChatTheme
  prefillDraft?: string
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr.includes('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z')
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' })
}

async function getOrCreateChat(personaName: string, userName: string, token?: string, _communityId?: string): Promise<string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  // Use findOrCreate — one persistent 1:1 thread per relationship
  const createRes = await fetch('/api/conversations', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      find_or_create: true,
      participants: [userName, personaName],
    }),
  })
  const data = await createRes.json()
  return data.id
}

export default function HomeChat({ personaName, displayName, theme, prefillDraft }: Props) {
  const { user } = useAuth()
  const sseOk = useSSEConnected()
  const chatUserId = user?.loginName?.toLowerCase() || user?.userName?.toLowerCase() || 'joel'
  const chatUserName = user?.loginName || user?.userName || 'joel'
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const [chatId, setChatId] = useState<string | null>(null)
  const [olderMessages, setOlderMessages] = useState<Message[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Prefill draft from external context (e.g. Awareness page reflection quote)
  const prefillApplied = useRef(false)
  useEffect(() => {
    if (prefillDraft && !prefillApplied.current) {
      setDraft(prefillDraft)
      prefillApplied.current = true
      // Auto-resize the textarea after prefill
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.style.height = 'auto'
          inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px'
          inputRef.current.focus()
          // Place cursor at end
          inputRef.current.selectionStart = inputRef.current.selectionEnd = inputRef.current.value.length
        }
      }, 200)
    }
  }, [prefillDraft])

  // Get or create the persistent chat
  useEffect(() => {
    getOrCreateChat(personaName, chatUserId, token || undefined, user?.communityId || undefined).then(setChatId)
  }, [personaName, chatUserId, token, user?.communityId])

  // Fetch recent messages (last 100)
  const { data: msgData } = useQuery({
    queryKey: ['homeChat', chatId],
    queryFn: async () => {
      const res = await fetch(`/api/conversations/${chatId}/messages?limit=500`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const data = await res.json()
      const msgs = Array.isArray(data) ? data : data.messages || []
      setHasMore(data.hasMore ?? false)
      return msgs
    },
    enabled: !!chatId,
    refetchInterval: sseOk ? false : 15_000,
  })

  const recentMessages: Message[] = msgData || []
  const messages: Message[] = [...olderMessages, ...recentMessages]

  // Reset older messages when switching chats
  useEffect(() => { setOlderMessages([]); setHasMore(false) }, [chatId])

  // Load older messages when scrolling to top
  const loadOlderMessages = useCallback(async () => {
    if (loadingMore || !hasMore || !chatId || messages.length === 0) return
    setLoadingMore(true)
    const oldestId = messages[0]?.id
    try {
      const res = await fetch(`/api/conversations/${chatId}/messages?limit=50&before=${oldestId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const data = await res.json()
      const older = Array.isArray(data) ? data : data.messages || []
      setHasMore(data.hasMore ?? false)
      if (older.length > 0) {
        setOlderMessages(prev => [...older, ...prev])
      }
    } catch (e) {
      console.error('[HomeChat] load older error:', e)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, hasMore, chatId, messages, token])

  // Scroll-to-top detection
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const handleScroll = () => {
      if (el.scrollTop < 80 && hasMore && !loadingMore) {
        loadOlderMessages()
      }
    }
    el.addEventListener('scroll', handleScroll)
    return () => el.removeEventListener('scroll', handleScroll)
  }, [hasMore, loadingMore, loadOlderMessages])

  // Auto-scroll on new messages
  const prevCount = useRef(0)
  useEffect(() => {
    if (messages.length > prevCount.current) {
      // Double RAF to ensure DOM has updated with new message height
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
        })
      })
    }
    prevCount.current = messages.length
  }, [messages.length])

  // Scroll to bottom on initial load
  useEffect(() => {
    if (messages.length > 0 && prevCount.current === 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView()
      }, 100)
    }
  }, [messages.length])

  // Send message with optimistic update
  const sendMut = useMutation({
    mutationFn: async (content: string) => {
      const res = await fetch(`/api/conversations/${chatId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ content, sender: chatUserId }),
      })
      return res.json()
    },
    onMutate: async (content: string) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['homeChat', chatId] })
      // Snapshot previous messages
      const prev = queryClient.getQueryData(['homeChat', chatId])
      // Optimistically add the new message
      queryClient.setQueryData(['homeChat', chatId], (old: Message[] | undefined) => [
        ...(old || []),
        {
          id: 'optimistic-' + Date.now(),
          sender: chatUserName,
          sender_id: chatUserId,
          sender_name: chatUserName,
          content,
          created_at: new Date().toISOString(),
        },
      ])
      return { prev }
    },
    onError: (_err, _content, context) => {
      // Revert on error
      if (context?.prev) queryClient.setQueryData(['homeChat', chatId], context.prev)
    },
    onSettled: () => {
      // Refetch to get the real server state
      queryClient.invalidateQueries({ queryKey: ['homeChat', chatId] })
    },
    onSuccess: () => {
      setDraft('')
      if (inputRef.current) {
        inputRef.current.style.height = 'auto'
      }
    },
  })

  const handleSend = useCallback(() => {
    if (!draft.trim() || !chatId) return
    sendMut.mutate(draft.trim())
  }, [draft, chatId, sendMut])

  if (!chatId) {
    return (
      <div className="rounded-xl p-8 text-center" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Setting up chat...</div>
      </div>
    )
  }

  return (
    <div
      className="overflow-hidden flex flex-col h-full"
      style={{
        background: theme.bgGradient,
      }}
    >
      {/* Chat header */}
      <div
        className="px-4 py-3 flex items-center gap-3 shrink-0"
        style={{ borderBottom: `1px solid ${theme.accentColor}22` }}
      >
        <PersonaAvatar name={personaName} size={28} />
        <span className="text-sm font-medium" style={{ color: theme.accentColor }}>
          Chat with {displayName}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {messages.length} messages
        </span>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
      >
        {loadingMore && (
          <div className="text-center py-2">
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading older messages...</div>
          </div>
        )}

        {hasMore && !loadingMore && (
          <div className="text-center py-2">
            <button
              onClick={loadOlderMessages}
              className="text-xs px-3 py-1 rounded-full"
              style={{ color: theme.accentColor, border: `1px solid ${theme.accentColor}44`, background: 'transparent' }}
            >
              Load older messages
            </button>
          </div>
        )}

        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="text-xs italic" style={{ color: 'var(--text-muted)' }}>
              Welcome to {displayName}'s space. Say something.
            </div>
          </div>
        )}

        {messages.map(msg => {
          const isPersona = msg.sender === personaName
          const isSystem = msg.is_system

          if (isSystem) {
            return (
              <div key={msg.id} className="text-center">
                <span className="text-xs italic" style={{ color: 'var(--text-muted)' }}>
                  {msg.content}
                </span>
              </div>
            )
          }

          return (
            <div
              key={msg.id}
              className="flex gap-2.5"
              style={{ flexDirection: isPersona ? 'row' : 'row-reverse' }}
            >
              <PersonaAvatar name={msg.sender} size={28} />
              <div
                className="rounded-xl px-3.5 py-2.5 max-w-[75%]"
                style={{
                  background: isPersona ? theme.myBubbleBg : theme.visitorBubbleBg,
                  borderLeft: isPersona ? `2px solid ${theme.myBubbleBorder}` : undefined,
                  borderRight: !isPersona ? '2px solid var(--border)' : undefined,
                }}
              >
                <div className="text-sm leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                  <MarkdownRenderer content={msg.content} />
                </div>
                <div className="text-right mt-1">
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    {formatTime(msg.created_at)}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 shrink-0" style={{ borderTop: `1px solid ${theme.accentColor}22` }}>
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={e => {
              setDraft(e.target.value)
              e.target.style.height = 'auto'
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'
            }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
            placeholder={`Say something to ${displayName}...`}
            rows={1}
            className="flex-1 px-3.5 py-2 rounded-xl text-sm outline-none resize-none"
            style={{
              background: theme.inputBg,
              color: 'var(--text-primary)',
              border: '1px solid var(--border)',
              maxHeight: 100,
              transition: 'box-shadow 0.2s',
            }}
            onFocus={e => { (e.target as HTMLElement).style.boxShadow = `0 0 0 2px ${theme.inputFocusGlow}` }}
            onBlur={e => { (e.target as HTMLElement).style.boxShadow = 'none' }}
          />
          <button
            onClick={handleSend}
            disabled={!draft.trim() || sendMut.isPending}
            className="px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-30 shrink-0"
            style={{
              background: draft.trim() ? theme.accentColor : 'var(--bg-elevated)',
              color: draft.trim() ? '#0d1117' : 'var(--text-muted)',
              border: 'none',
              cursor: draft.trim() ? 'pointer' : 'default',
            }}
          >
            Send
          </button>
        </div>
      </div>

      {/* Chatter audio player — plays voice clips in the home chat */}
      {chatId && <ChatterPlayer conversationId={chatId} chatterEnabled={true} />}
    </div>
  )
}
